import {Moment} from 'moment';
import React from 'react';

import {calendarDidMount, calendarDidUpdate} from 'calendar/CalendarComponentUtil';
import {
  CalendarEntriesByDate,
  CalendarFilesByDate,
  CalendarInteraction,
  CalendarProcessTasksByDate,
  CalendarProject,
  CalendarProjectsByDate,
  CalendarRequestsByDate,
  CalendarView as CalendarViewType,
  LayeredCalendarItems,
  ViewRange,
} from 'calendar/CalendarConstants';
import CalendarSidebarFilter from 'calendar/CalendarSidebarFilter';
import CalendarStore from 'calendar/CalendarStore';
import CalendarToolbar from 'calendar/CalendarToolbar';
import CalendarView from 'calendar/CalendarView';
import DataConnector from 'connect/DataConnector';
import EntityStore from 'stores/EntityStore';
import * as nyc from 'ui-library/NYC';

import {Member} from './OrganizationConstants';

interface Props {
  organizationId: string;
}

interface EnhancedProps {
  allProjects: Array<CalendarProject>;
  currentView: CalendarViewType;
  hiddenProjectIds: Array<string>;
  hiddenMemberIds: Array<string>;
  interact: CalendarInteraction;
  isLoadingData: boolean;
  today: Moment;
  viewLabel: string;
  viewRange: ViewRange;
  visibleDate: Moment;
  projects: CalendarProjectsByDate;
  layeredProjects: LayeredCalendarItems<CalendarProject>;
  entries: CalendarEntriesByDate;
  files: CalendarFilesByDate;
  requests: CalendarRequestsByDate;
  processTasks: CalendarProcessTasksByDate;
  members: Array<Member> | null;
}

class OrganizationCalendarTabConnector extends DataConnector<Props, EnhancedProps> {
  override shouldSubscribeToRedux = false;
  override stores = [CalendarStore, EntityStore];

  enhanceProps(): EnhancedProps {
    return {
      allProjects: this.calendarStore().getAllProjects(),
      currentView: this.calendarStore().getCurrentView(),
      hiddenProjectIds: this.calendarStore().getHiddenProjectIds(),
      hiddenMemberIds: this.calendarStore().getHiddenMemberIds(),
      interact: this.calendarStore().getInteract(),
      isLoadingData: this.calendarStore().isLoadingData(),
      today: this.calendarStore().getToday(),
      viewLabel: this.calendarStore().getViewLabel(),
      viewRange: this.calendarStore().getViewRange(),
      visibleDate: this.calendarStore().getVisibleDate(),
      projects: this.calendarStore().getProjects(),
      layeredProjects: this.calendarStore().getLayeredProjects(),
      entries: this.calendarStore().getEntries(),
      files: this.calendarStore().getNonEntryFiles(),
      requests: this.calendarStore().getRequests(),
      processTasks: this.calendarStore().getProcessTasks(),
      members: this.entityStore().getMembersForGroup(this.props.organizationId),
    };
  }
}

class OrganizationCalendarTab extends React.Component<Props & EnhancedProps> {
  override componentDidMount(): void {
    calendarDidMount(this.props.viewRange, this.props.currentView, {
      organizationId: this.props.organizationId,
    });
  }

  override componentDidUpdate(prevProps: Props & EnhancedProps): void {
    calendarDidUpdate(
      prevProps.viewRange,
      this.props.viewRange,
      prevProps.currentView,
      this.props.currentView,
      {organizationId: this.props.organizationId}
    );
  }

  override render(): JSX.Element {
    return (
      <nyc.Row className="u-small-spacing">
        <nyc.Col lg={3}>
          <CalendarSidebarFilter
            hiddenProjectIds={this.props.hiddenProjectIds}
            hiddenMemberIds={this.props.hiddenMemberIds}
            projects={this.props.allProjects}
            members={this.props.members}
          />
        </nyc.Col>
        <nyc.Col lg={18}>
          <CalendarToolbar
            currentView={this.props.currentView}
            isLoadingData={this.props.isLoadingData}
            today={this.props.today}
            viewRange={this.props.viewRange}
            viewLabel={this.props.viewLabel}
            visibleDate={this.props.visibleDate}
          />
          <div className="organizationCalendar u-spacing-top">
            <CalendarView
              currentView={this.props.currentView}
              entries={this.props.entries}
              requests={this.props.requests}
              processTasks={this.props.processTasks}
              files={this.props.files}
              interact={this.props.interact}
              isLoadingData={this.props.isLoadingData}
              layeredItems={this.props.layeredProjects}
              projects={this.props.projects}
              showWeeklyFileActivity={false}
              today={this.props.today}
              viewRange={this.props.viewRange}
              visibleDate={this.props.visibleDate}
            />
          </div>
        </nyc.Col>
      </nyc.Row>
    );
  }
}

export default OrganizationCalendarTabConnector.connect(OrganizationCalendarTab);
